import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { useGetFaqCategoriesQuery, useGetFaqNextCategoriesQuery, useGetTopQuestionsQuery } from '@/api/sanity/generated';
import { Layout } from '@/components/layout';
import HeroSimple from '@/sections/HeroSimple';
import { FIELD_FULLTEXT, FaqSearch } from '@/sections/faq/FaqSearch';
import { GroupedCategory } from '@/sections/faq/GroupedCategory';
import { OrderedFaqCategories, orderFaqCategories, orderHUFaqCategories, orderSKFaqCategories } from '@/sections/faq/functions/orderFaqCategories';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { useTranslation } from 'react-i18next';
import IconSkippay from '@/assets/icons/heroIcons/Skippay.svg';
import IconShoppingCart from '@/assets/icons/heroIcons/Shopping-cart.svg';
import IconAdjustments from '@/assets/icons/heroIcons/Adjustments.svg';
import IconCollection from '@/assets/icons/heroIcons/Collection.svg';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import { TopQuestions } from '@/sections/faq/TopQuestions';
import { type FixedLengthArray } from '@/types/fixedLengthArray';
import { type LinkQuestionProps } from '@/sections/faq/LinkQuestion';
import { Form, FormSpy } from 'react-final-form';
import { addPrefixToDataParams } from '@/utils/string';
import { type SearchFormValues, type SearchResult, useFaqSearch } from '@/sections/faq/hooks/useFaqSearch';
import { prepareTopQuestions } from '@/sections/faq/functions/prepareTopQuestions';
import { prepareSearchableItems } from '@/sections/faq/functions/prepareSearchableItems';
import Loader from '@/components/Loader';
import { ErrorSection } from '@/components/Error';
import { SearchResults } from '@/sections/faq/SearchResults';
import { Category } from '@/sections/faq/NextCategory';
import { SeoHeader } from '@/components/SeoHeader';
import React from 'react';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { sanityLanguage } from '@/constants/language';
interface PageProps extends IntlProps {
  categories: OrderedFaqCategories;
  topQuestions: FixedLengthArray<LinkQuestionProps & {
    categorySlug: string;
  }, 3> | null;
  searchableItems: SearchResult[];
  noResultCategories: Category[];
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page(props) {
  const $ = _c(27);
  usePageView("faq");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["core", "faq"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    menuPromoBanners,
    searchableItems,
    topQuestions,
    categories,
    noResultCategories
  } = props;
  const {
    query,
    formValues,
    setFilter,
    searchForm
  } = useFaqSearch(searchableItems);
  let t1;
  if ($[1] !== t) {
    t1 = t("faq:seo.title");
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t) {
    t2 = t("faq:seo.description");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <SeoHeader title={t1} description={t2} />;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== categories || $[9] !== formValues || $[10] !== noResultCategories || $[11] !== query.data || $[12] !== query.isError || $[13] !== query.isPending || $[14] !== setFilter || $[15] !== t) {
    t4 = t5 => {
      const {
        form,
        handleSubmit
      } = t5;
      return <><form onSubmit={handleSubmit} {...addPrefixToDataParams({
          analyticsParam: "faqSearch",
          testId: "faqSearch",
          prefix: "form"
        })}><FormSpy subscription={{
            values: true
          }} onChange={t6 => {
            const {
              values
            } = t6;
            return setFilter(values);
          }} /><HeroSimple titleClassName="lg:text-8xl" circleClassName="left-auto right-0 lg:-translate-y-1/3 lg:translate-x-1/3" title={t("faq:title")} className="bg-blue-50"><FaqSearch initialValue={formValues[FIELD_FULLTEXT]} onClean={() => form.change(FIELD_FULLTEXT, undefined)} /></HeroSimple><div className="relative bg-blue-50">{query.isError ? <ErrorSection variant="basic" /> : query.isPending ? <Loader /> : query.data ? <SearchResults categories={query.data.categories} questions={query.data.questions} noResultCategories={noResultCategories} onClean={() => form.change(FIELD_FULLTEXT, undefined)} /> : <main className="relative z-10 mx-auto grid max-w-292 gap-8 px-4 pb-40 md:grid-cols-2"><GroupedCategory category={categories.productsAndServices} name={t("faq:category.productsAndServices")} icon={<IconSkippay className="h-12 w-12 text-blue-500" />} /><GroupedCategory category={categories.shopping} name={t("faq:category.shopping")} icon={<IconShoppingCart className="h-12 w-12 text-blue-500" />} /><GroupedCategory category={categories.settings} name={t("faq:category.settings")} icon={<IconAdjustments className="h-12 w-12 text-blue-500" />} />{categories.other && <GroupedCategory category={categories.other} name={t("faq:category.other")} icon={<IconCollection className="h-12 w-12 text-blue-500" />} />}</main>}<div className="bgCircle absolute bottom-4 left-12 hidden h-[25rem] w-[25rem] rotate-90 md:block" /></div></form></>;
    };
    $[8] = categories;
    $[9] = formValues;
    $[10] = noResultCategories;
    $[11] = query.data;
    $[12] = query.isError;
    $[13] = query.isPending;
    $[14] = setFilter;
    $[15] = t;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== searchForm || $[18] !== t4) {
    t5 = <Form form={searchForm} onSubmit={searchForm.submit}>{t4}</Form>;
    $[17] = searchForm;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== topQuestions) {
    t6 = topQuestions && topQuestions.length > 0 && <><SectionDelimiter color="bg-blue-100" background="bg-blue-50" /><div className="relative overflow-hidden bg-blue-100"><div className="relative z-10"><TopQuestions topQuestions={topQuestions} /></div></div></>;
    $[20] = topQuestions;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== menuPromoBanners || $[23] !== t3 || $[24] !== t5 || $[25] !== t6) {
    t7 = <Layout menuPromoBanners={menuPromoBanners}>{t3}{t5}{t6}</Layout>;
    $[22] = menuPromoBanners;
    $[23] = t3;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7;
}
const topQuestionIds = () => {
  if (process.env.NEXT_PUBLIC_LANGUAGE === 'sk' || process.env.NEXT_PUBLIC_LANGUAGE === 'hu') {
    return [];
  }
  if (process.env.NEXT_PUBLIC_TARGET_ENV === 'accept' || process.env.NEXT_PUBLIC_TARGET_ENV === 'nightly') {
    return ['61B221FiwEB3aWLRk375U4', '2LsmOSWuEKRijzruEwGlTd', 'Sauilz6nFWLXQM6JkzzB4'];
  }
  return ['1YfpbvslNnh6zDWV0RmAjn', '5V8NBBraybLW5SvYMRzJto', '6nhO5WVbVLzQLrX3RNiGB0'];
};
const noResultCategoryIds = () => {
  if (process.env.NEXT_PUBLIC_LANGUAGE === 'hu') {
    return ['d97454e4-3ad8-449f-a190-5f2d7b592a5b', '2dd27096-53ce-4787-8e05-b4f818d9032a', 'd3b1f108-a23b-4a72-96bf-beac131a8b2a', '3a390f22-231f-48dd-a880-4c7adbae869a'];
  }
  if (process.env.NEXT_PUBLIC_LANGUAGE === 'sk') {
    return ['6BfMs6m5qGEYn3qsOWCBbG', '6Iyu1doyxlpMLwhkPsujmK', '2DwUXOK4Tws9NuPXiKBTbl', '12vL2KlrTy7tqbc1qWLwI4'];
  }
  if (process.env.NEXT_PUBLIC_TARGET_ENV === 'accept' || process.env.NEXT_PUBLIC_TARGET_ENV === 'nightly') {
    return ['7iCsxLitHoUqqRyE9ULjwV', '3mOvmE8yTjrrDsZJJyX4tJ', '4Q2CmNtyqNsLWG3t61wgJN', '6nV2Tpb7PwTJiS4RSLte7Z'];
  }
  return ['7iCsxLitHoUqqRyE9ULjwV', '3mOvmE8yTjrrDsZJJyX4tJ', '4nQV5zahsGsZ7TiGXSSWbZ', '6nV2Tpb7PwTJiS4RSLte7Z'];
};
export const getStaticProps = (async ctx => {
  const fetchAllCategories = useGetFaqCategoriesQuery.fetcher({
    i18nLang: sanityLanguage
  });
  const fetchNoResultCategories = useGetFaqNextCategoriesQuery.fetcher({
    ids: noResultCategoryIds(),
    i18nLang: sanityLanguage
  });
  const fetchTopQuestions = useGetTopQuestionsQuery.fetcher({
    questionIds: topQuestionIds(),
    i18nLang: sanityLanguage
  });
  const categories = await fetchAllCategories();
  const topQuestions = await fetchTopQuestions();
  const noResultCategories = await fetchNoResultCategories();
  return {
    props: {
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      categories: process.env.NEXT_PUBLIC_LANGUAGE === 'sk' ? orderSKFaqCategories(categories.allFaqCategory?.filter(item => item && item.name) ?? []) : process.env.NEXT_PUBLIC_LANGUAGE === 'hu' ? orderHUFaqCategories(categories.allFaqCategory?.filter(item => item && item.name) ?? []) : orderFaqCategories(categories.allFaqCategory.filter(item => item && item.name) ?? []),
      topQuestions: await prepareTopQuestions(topQuestions),
      searchableItems: await prepareSearchableItems(categories.allFaqCategory ?? []),
      noResultCategories: (noResultCategories.allFaqCategory?.filter(category => category && category.name && category.slug) ?? []) as Category[],
      ...(await getIntlProps(ctx, ['faq']))
    }
  };
}) satisfies GetStaticProps<PageProps>;